import { MailLanguage } from "../../emailLanguage";
import { ApiFailure, ApiResult, postJson } from "../../restClient";

/**
 * The login-free withdrawal endpoints of § 356a BGB - contract with
 * `backend/GrooveGrid.Api/Controllers/WithdrawalController.cs`:
 *
 *   POST /api/withdrawal/preview   -> WithdrawalPreview
 *   POST /api/withdrawal/confirm   -> WithdrawalReceipt   (rate limited, 429)
 *
 * Both take a WithdrawalSubmissionBody and neither is behind a token: the
 * function has to be usable by anyone for the whole withdrawal period.
 *
 * Enums travel by name, as in the cancellation (`CancellationController` sends
 * `Problem.ToString()`): `problem` is a WithdrawalInputProblem, `outcome` a
 * WithdrawalOutcome. An unknown name is treated like a failure, never a success.
 *
 * ⚠️ The backend may answer a rejected declaration either with 200 and a
 * `problem` other than "None" or with a 4xx/5xx and the same body. Both are
 * folded into one ApiFailure below, so the page has a single error path.
 *
 * ⚠️ Like the cancellation, neither endpoint says whether an address belongs to
 * a customer. The page renders only what the person typed.
 */

/** What the person typed. § 356a Abs. 1 Satz 2 BGB, nothing more. */
export interface WithdrawalFormValues {
  /** Nr. 1 - the name of the consumer. */
  name: string;
  /** Nr. 3 - where the receipt of § 356a Abs. 2 goes. */
  email: string;
  /** Nr. 2 - the contract being withdrawn from, in the person's own words. */
  contractReference: string;
  /** Optional free text. No reason is asked; a withdrawal needs none. */
  note: string;
}

/** The body both endpoints take. */
export interface WithdrawalSubmissionBody {
  name: string;
  email: string;
  contractReference: string;
  note: string;
  /** "de" or "en" - picks the template the receipt is written in. */
  language: MailLanguage;
}

/** `WithdrawalInputProblem` by name. */
export type WithdrawalInputProblem =
  | "None"
  | "NameMissing"
  | "EmailMissing"
  | "EmailMalformed"
  | "ContractReferenceMissing"
  | "NameTooLong"
  | "EmailTooLong"
  | "ContractReferenceTooLong"
  | "NoteTooLong";

/** `WithdrawalOutcome` by name. */
export type WithdrawalOutcome = "Received" | "InvalidInput" | "Failed";

/** The declaration as it will be recorded - trimmed, otherwise untouched. */
export interface WithdrawalDeclaration {
  name: string | null;
  email: string | null;
  contractReference: string | null;
  note: string | null;
}

/** The confirmation page, as the backend hands it back. */
export interface WithdrawalPreview {
  problem: WithdrawalInputProblem | string;
  /** Null whenever `problem` is not "None". */
  declaration: WithdrawalDeclaration | null;
  /** Always true - the page has to say that the button IS the withdrawal. */
  effectiveOnSubmit: boolean;
  /** Where the confirmation posts to, for documentation. */
  confirmPath: string;
  /** The address a withdrawal can always be sent to instead. */
  contactEmail: string;
}

/** The answer to a submitted withdrawal. */
export interface WithdrawalReceipt {
  outcome: WithdrawalOutcome | string;
  problem: WithdrawalInputProblem | string;
  /** Reference of the recorded declaration, quoted back to the person. */
  requestId: string;
  /**
   * When the declaration was received, ISO-8601 UTC. § 356a Abs. 2 wants date
   * and time of receipt confirmed, so the page shows both.
   */
  receivedAt: string;
}

/**
 * Names of `WithdrawalInputProblem` that point at a single field, mapped onto
 * that field. Anything else is shown as a general failure.
 */
export const PROBLEM_FIELDS: Record<string, keyof WithdrawalFormValues> = {
  NameMissing: "name",
  NameTooLong: "name",
  EmailMissing: "email",
  EmailMalformed: "email",
  EmailTooLong: "email",
  ContractReferenceMissing: "contractReference",
  ContractReferenceTooLong: "contractReference",
  NoteTooLong: "note",
};

/** The problem key the page shows when the backend could not store it. */
export const FAILED_PROBLEM = "Failed";

/** Whatever a failed response carried, read loosely. */
interface WithdrawalProblemBody {
  problem?: string;
  outcome?: string;
  contactEmail?: string;
}

const toBody = (
  values: WithdrawalFormValues,
  language: MailLanguage,
): WithdrawalSubmissionBody => ({
  name: values.name.trim(),
  email: values.email.trim(),
  contractReference: values.contractReference.trim(),
  note: values.note.trim(),
  language,
});

const hasProblem = (problem: string | null | undefined): problem is string =>
  !!problem && problem !== "None";

/**
 * Puts the name the page looks up into `body.problem`: an input problem when
 * there is one, otherwise "Failed" when the outcome says the backend could not
 * store the declaration.
 */
const normaliseFailure = (failure: ApiFailure): ApiFailure => {
  const body = (failure.body || {}) as WithdrawalProblemBody;
  const problem = hasProblem(body.problem)
    ? body.problem
    : body.outcome === FAILED_PROBLEM
      ? FAILED_PROBLEM
      : undefined;

  return {
    ...failure,
    body: failure.body ? { ...failure.body, problem } : null,
  };
};

/** A 200 that still carries a rejection, turned into the failure it is. */
const rejected = (
  reason: ApiFailure["reason"],
  problem: string,
  contactEmail: string | undefined,
): ApiFailure => ({
  ok: false,
  reason,
  status: 200,
  body: { problem, contactEmail },
});

/**
 * Builds the confirmation page. Hands back only what was typed, so the person
 * confirms the exact wording that will be recorded. Never throws.
 */
export const previewWithdrawal = async (
  values: WithdrawalFormValues,
  language: MailLanguage,
): Promise<ApiResult<WithdrawalPreview>> => {
  const result = await postJson<WithdrawalPreview>(
    "withdrawal/preview",
    toBody(values, language),
  );

  if (!result.ok) {
    return normaliseFailure(result);
  }

  if (hasProblem(result.data.problem)) {
    return rejected("invalid", result.data.problem, result.data.contactEmail);
  }

  if (!result.data.declaration) {
    return rejected("server", FAILED_PROBLEM, result.data.contactEmail);
  }

  return result;
};

/**
 * Submits the withdrawal. This IS the withdrawal - received the moment it
 * returns "Received", with the receipt mail queued. Never throws.
 *
 * ⚠️ Only an explicit "Received" with a reference and a timestamp counts: the
 * result screen states a legal fact and must not infer it from anything less.
 */
export const confirmWithdrawal = async (
  values: WithdrawalFormValues,
  language: MailLanguage,
): Promise<ApiResult<WithdrawalReceipt>> => {
  const result = await postJson<WithdrawalReceipt>(
    "withdrawal/confirm",
    toBody(values, language),
  );

  if (!result.ok) {
    return normaliseFailure(result);
  }

  const { outcome, problem, requestId, receivedAt } = result.data;

  if (outcome === "Received" && requestId && receivedAt) {
    return result;
  }

  if (outcome === "InvalidInput" && hasProblem(problem)) {
    return rejected("invalid", problem, undefined);
  }

  return rejected("server", FAILED_PROBLEM, undefined);
};
